<script>
import { mapGetters, mapActions, mapState } from "vuex";
import NavigationWrapper from "@/components/NavigationWrapper";
import DeleteTeamModal from "@/pages/Team/General/DeleteTeamModal";
import LeaveTeamModal from "@/pages/Team/General/LeaveTeamModal";
import EditTeamModal from "@/pages/Team/General/EditTeamModal";

export default {
  // Included components
  components: {
    NavigationWrapper,
    DeleteTeamModal,
    LeaveTeamModal,
    EditTeamModal,
  },

  computed: {
    // Whether the user is the only owner of the team (implies that they
    // are an owner/admin)
    isNotOnlyOwner() {
      return (
        !this.activeRole ||
        !this.activeRole.owner ||
        !Object.values(this.teamMembers).filter((member) => {
          return member.role.owner;
        }).length == 1
      );
    },

    // Mapped vuex stat
    ...mapState(["teamMembers", "user"]),
    ...mapGetters(["activeTeam", "activeRole", "can"]),
  },

  // When the component is created, refetch the user's teams
  async created() {
    await Promise.all([this.getTeams(), this.getAccountRoles()]);
    if (this.activeRole.owner && this.can.members.view) {
      this.getTeamMembers([
        "account.id,account.name,account.handle,account.email,role.name",
      ]);
    }
  },

  methods: {
    // Mapped vuex actions
    ...mapActions(["getTeams", "getTeamMembers", "getAccountRoles"]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item active>Settings</b-breadcrumb-item>
    </template>
    <b-row>
      <b-col cols="8">
        <h1 class="mb-3 text-overflow">{{ activeTeam.name }}</h1>
      </b-col>
      <b-col cols="4" align-self="center">
        <span v-if="activeTeam.personal">
          <span
            id="disabled-wrapper"
            class="d-inline-block float-right"
            tabindex="0"
          >
            <b-button disabled variant="danger">Delete Team</b-button>
          </span>
          <b-tooltip target="disabled-wrapper">
            You cannot delete a personal team.
          </b-tooltip>
        </span>
        <b-button
          v-else-if="can.team.manage && activeTeam.member_count == 1"
          key="delete"
          class="float-right"
          variant="danger"
          v-b-modal.delete-team-modal
        >
          Delete Team
        </b-button>
        <b-button
          v-else-if="isNotOnlyOwner"
          key="leave"
          class="float-right"
          variant="danger"
          v-b-modal.leave-team-modal
        >
          Leave Team
        </b-button>
        <span v-else>
          <span
            id="disabled-wrapper"
            class="d-inline-block float-right"
            tabindex="0"
          >
            <b-button disabled variant="danger">Leave Team</b-button>
          </span>
          <b-tooltip target="disabled-wrapper">
            You must give another team member the Admin role before leaving.
          </b-tooltip>
        </span>
        <b-button
          v-if="activeTeam.personal || can.team.manage"
          class="float-right mr-2"
          variant="primary"
          v-b-modal.edit-team-modal
        >
          Edit
        </b-button>
      </b-col>
    </b-row>

    <b-card>
      <b-card-text>
        <p>
          <b>Team Name</b>
          {{ activeTeam.name }}
        </p>
        <p>
          <b>Team Handle</b>
          {{ activeTeam.handle }}
        </p>
      </b-card-text>
    </b-card>
    <DeleteTeamModal modal-id="delete-team-modal" v-if="can.team.manage" />
    <LeaveTeamModal modal-id="leave-team-modal" />
    <EditTeamModal modal-id="edit-team-modal" />
  </NavigationWrapper>
</template>

<style scoped>
.text-overflow {
  word-break: break-all;
}
</style>
